import { EMPTY_STR } from "@/constants/string";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";

/**
 * Replaces placeholders in a template string with corresponding values from an object.
 *
 * @param {string} template - The string containing placeholders (e.g., "Hello, {{name}}!").
 * @param {object} replaces - An object where keys are placeholders and values are replacements (e.g., { name: "Alice" }).
 * @returns {string} - The resulting string with placeholders replaced by actual values.
 */
export const replaceStr = (template: string, replaces: object): string => {
  return Object.entries(replaces).reduce((cur, [key, value]) => (value !== undefined ? cur.replace(`{{${key}}}`, value.toString()) : cur), template);
};

/**
 * Constructs a full name from an object with optional middle name and nickname.
 *
 * @param {object} objectName - An object containing name parts.
 * @param {string} objectName.lastName - The last name.
 * @param {string | null} [objectName.middleName] - The middle name (optional).
 * @param {string} objectName.firstName - The first name.
 * @param {string | null} [objectName.nickName] - The nickname (optional).
 * @returns {string} - The full name, or the nickname if provided.
 */
export const getFullName = (objectName: {
  lastName?: string | null;
  middleName?: string | null;
  firstName: string;
  nickName?: string | null;
}): string => {
  const { lastName, middleName, firstName, nickName } = objectName;

  if (nickName) return nickName;

  return [lastName ?? [], middleName ?? [], firstName].flat().join(" ");
};

export const getFullAddress = (addressObject: {
  zipCode?: string | null;
  province?: string | null;
  municipality?: string | null;
  address?: string | null;
}): string => {
  const { zipCode, province, municipality, address } = addressObject;

  const formattedZipCode = zipCode && zipCode.trim().length ? `〒${zipCode.replace(/^(\d{3})(\d{4})$/, "$1-$2")} ` : "";

  const fullAddress = [province ?? [], municipality ?? [], address ?? []].flat().join("").trim();

  return fullAddress.length ? formattedZipCode + fullAddress : EMPTY_STR.TEXT;
};

/**
 * Converts a value to a string. Returns an empty string if the value is not a string.
 *
 * @param {unknown} data - The value to convert.
 * @returns {string} - The string representation of the value, or an empty string if the value is not a string.
 */
export const toString = (data: unknown): string => {
  if (typeof data === "string") return data;
  return "";
};

/**
 * Truncates a string with an ellipsis if it exceeds a given length
 * @param {string} str - The input string to truncate
 * @param {number} maxLength - The maximum allowed length for the string
 * @returns {string} - The truncated string with an ellipsis (if needed)
 */
export const truncateWithEllipsis = (str: string, maxLength: number): string => {
  // Check if the string needs truncation
  if (str.length <= maxLength) {
    return str;
  }
  // Truncate the string and add an ellipsis
  return str.slice(0, maxLength - 3) + "...";
};

/**
 * Truncates `text` to fit inside `el` (a fixed-width, overflow-hidden element) at a
 * WHOLE-character boundary, appending a baseline ASCII "...". Measures with the element's
 * own layout (scrollWidth vs clientWidth) — no canvas. Mutates el.textContent and returns
 * whether it was truncated. Avoids the half-cut glyph of pure overflow:hidden and the
 * JP-centered U+2026 of CSS text-overflow.
 */
export const truncateElementToWidth = (el: HTMLElement, text: string): boolean => {
  el.textContent = text;
  if (el.scrollWidth <= el.clientWidth) return false;

  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    el.textContent = text.slice(0, mid) + "...";
    if (el.scrollWidth <= el.clientWidth) lo = mid;
    else hi = mid - 1;
  }
  el.textContent = text.slice(0, lo) + "...";
  return true;
};

/**
 * Truncates a file name with ellipsis in the middle (like macOS)
 * Shows the beginning and end of the filename with "..." in the middle
 * @param {string} fileName - The file name to truncate
 * @param {number} maxLength - The maximum allowed length for the file name
 * @returns {string} - The truncated file name with ellipsis in the middle (if needed)
 */
export const truncateFileNameMiddle = (fileName: string, maxLength: number = 30): string => {
  if (!fileName || fileName.length <= maxLength) {
    return fileName;
  }

  const lastDotIndex = fileName.lastIndexOf(".");
  const hasExtension = lastDotIndex > 0 && lastDotIndex < fileName.length - 1;

  let nameWithoutExt = fileName;
  let extension = "";

  if (hasExtension) {
    nameWithoutExt = fileName.slice(0, lastDotIndex);
    extension = fileName.slice(lastDotIndex);
  }

  const availableLength = maxLength - 3 - extension.length;

  if (availableLength <= 0) {
    return truncateWithEllipsis(fileName, maxLength);
  }

  const startLength = Math.ceil(availableLength * 0.6);
  const endLength = Math.floor(availableLength * 0.4);

  const start = nameWithoutExt.slice(0, startLength);
  const end = nameWithoutExt.slice(-endLength);

  return `${start}...${end}${extension}`;
};

export const padZeroIfFive = (input: string) => {
  const str = input.toString();
  return str.length === 5 ? "0" + str : str;
};

export const processTelNumber = (telNumber: string): { landlineTelNumber: string; mobileTelNumber: string } => {
  const noHyphenTelNumber = convertFullWidthToHalfWidth(telNumber);
  const isMobile = noHyphenTelNumber.startsWith("090") || noHyphenTelNumber.startsWith("080");

  return {
    landlineTelNumber: isMobile ? "" : telNumber,
    mobileTelNumber: isMobile ? telNumber : "",
  };
};

/**
 * Removes all HTML tags from a string and returns only the plain text content.
 *
 * @param {string} html - The HTML string to clean
 * @returns {string} - The plain text result
 */
export const stripHtml = (html: string): string => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

/**
 * Normalizes a string by:
 * - Converting fullwidth spaces to halfwidth spaces
 * - Replacing multiple consecutive line breaks with a single line break
 * - Replacing tabs (both halfwidth and fullwidth) with spaces
 * - Replacing multiple consecutive spaces with a single space
 * - Trimming leading and trailing whitespace
 *
 * @param {string | null | undefined} text - The text to normalize
 * @returns {string | null} - The normalized text, or null if input is null/undefined/empty
 */
export const normalizeString = (text: string | number | null | undefined): string | null => {
  if (text == null || text === "") return null;

  // Coerce number \u2192 string. BE occasionally returns numeric values (e.g.
  // talentNumber, zipCode) for fields the FE treats as text. Without this,
  // .replace would throw TypeError when the scan-modal update flow passes
  // talent-detail data straight to convertTalentFormDataReq.
  const str = typeof text === "string" ? text : String(text);

  return str
    .replace(/\u3000/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .replace(/\t+/g, " ")
    .replace(/ {2,}/g, " ")
    .trim();
};

/**
 * Normalizes a keyword string by:
 * - Trimming leading and trailing whitespace
 * - Replacing multiple consecutive spaces with a single space
 *
 * @param {string | null | undefined} keyword - The keyword to normalize
 * @returns {string} - The normalized keyword, or empty string if input is null/undefined
 */
export const normalizeKeyword = (keyword: string | null | undefined): string => {
  if (!keyword) return "";
  return keyword.trim().replace(/\s+/g, " ");
};

/**
 * Convert a time in the format 'HH:MM:SS' into the Japanese-style format 'H時M分'.
 */
export const convertTimeToJapaneseFormat = (timeStr: string): string => {
  if (!timeStr) return "";

  const [hour, minute] = timeStr.split(":");

  return `${Number(hour)}時${Number(minute)}分`;
};

/**
 * Truncates a given string to the hour and minute part if it is in the format "HH:mm:ss".
 * If the string is not in the format "HH:mm:ss", it will be returned as is.
 *
 * @param {string} value - The string to truncate
 * @returns {string} - The truncated string
 */
export const toHourMinuteFormat = (value: string) => {
  if (!value) {
    return "";
  }

  if (value.length === 8 && value.includes(":")) {
    return value.substring(0, 5);
  }

  return value;
};
